import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";

import type { Exercise } from "../../../src/types/contents/exercise";
import type { Workout } from "../../../src/types/contents/workout";
import {
  createWorkout,
  getContentList,
  getExercise,
  getWorkout,
  updateWorkout,
  uploadMedia,
} from "../api";
import { auth } from "../auth";

import { MediaWithPreview, TagsSelector } from "./Common";
import { MultiSelect } from "./MultiSelect";

const emptyWorkout: Workout<"api"> = {
  title: "",
  category: "",
  language: "",
  skillLevel: 0,
  objective: "",
  intensity: 0,
  duration: 0,
  media: "",
  description: "",
  exerciseRows: [],
  loadPoints: 0,
  calories: 0,
  tags: [],
  sportTitle: undefined,
  sportMETs: undefined,
  sportIcon: undefined,
  sportCategory: undefined,
  favorite: false,
};

const toOptions = (exercises: Exercise[]) => {
  return exercises.map((exercise) => ({
    label: exercise.title,
    value: exercise.id.toString(),
  }));
};

const ExerciseRow = ({
  row,
  exercises,
  onChange,
}: {
  row: Partial<Exercise<"row">>;
  exercises: Exercise[];
  onChange: (exerciseRow: Partial<Exercise<"row">>) => void;
}) => {
  if (!exercises || exercises.length === 0) {
    return null;
  }

  const setExercise = async (exerciseId: string) => {
    const exercise = await getExercise(exerciseId);
    if (exercise) {
      onChange({ ...row, exercise });
    }
  };

  const setAlternativeExercise = async (exerciseId: string) => {
    const alternativeExercise = await getExercise(exerciseId);
    if (alternativeExercise) {
      onChange({ ...row, alternativeExercise });
    }
  };

  const options = toOptions(exercises);

  return (
    <Stack spacing={2}>
      <MultiSelect
        label="Exercise"
        multiple={false}
        options={options}
        value={row.exercise?.id.toString() ?? ""}
        onChange={async (selected) => {
          void setExercise(selected[0]);
        }}
      />
      <MultiSelect
        label="Alternative Exercise"
        multiple={false}
        options={options}
        value={row.alternativeExercise?.id.toString() ?? ""}
        onChange={async (selected) => {
          void setAlternativeExercise(selected[0]);
        }}
      />
      <TextField
        label="Duration"
        type="number"
        value={row.duration ?? 0}
        onChange={(e) => {
          onChange({ ...row, duration: parseInt(e.currentTarget.value, 10) });
        }}
      />
      <TextField
        label="Distance"
        type="number"
        value={row.distance ?? 0}
        onChange={(e) => {
          onChange({ ...row, distance: parseInt(e.currentTarget.value, 10) });
        }}
      />
      <TextField
        label="Text"
        value={row.text ?? ""}
        onChange={(e) => {
          onChange({ ...row, text: e.currentTarget.value });
        }}
      />
    </Stack>
  );
};

const ExerciseRows = ({
  exerciseRows = [],
  onChange,
}: {
  exerciseRows?: Exercise<"row">[];
  onChange: (exerciseRows: Partial<Exercise<"row">>[]) => void;
}) => {
  const [exercises, setExercises] = useState<Exercise[]>([]);

  useEffect(() => {
    const init = async () => {
      setExercises(await getContentList<Exercise>("exercise"));
    };

    if (auth.currentUser) void init();
  }, []);

  return (
    <Stack>
      <Typography variant="subtitle1">Exercise Rows</Typography>
      {exerciseRows.map((row, index) => (
        <ExerciseRow
          key={index}
          row={row}
          exercises={exercises}
          onChange={(exerciseRow) => {
            const newRows = [...exerciseRows];
            // @ts-expect-error -- We know that newRows[index] exists, because we are iterating over exerciseRows
            newRows[index] = {
              ...exerciseRow,
            };
            onChange(newRows);
          }}
        />
      ))}
      <Button
        variant="contained"
        onClick={() => {
          const newRows = [...exerciseRows, {}];
          onChange(newRows);
        }}
      >
        Add new
      </Button>
    </Stack>
  );
};

export const WorkoutComponent = () => {
  const [workout, setWorkout] = useState<Workout<"api"> | null>(null);
  const [mediaPreview, setMediaPreview] = useState<File | null>(null);
  const [mediaIdOverride, setMediaIdOverride] = useState<boolean>(false);

  const { id } = useParams();
  const isEdit = id !== undefined && id !== "new";
  const navigate = useNavigate();

  useEffect(() => {
    const init = async () => {
      if (isEdit) {
        setWorkout(await getWorkout(id));
      } else {
        setWorkout({
          ...emptyWorkout,
        });
      }
    };

    if (auth.currentUser) void init();
  }, []);

  if (workout === null) {
    return (
      <Stack spacing={2}>
        <Typography variant="h4">Loading...</Typography>
      </Stack>
    );
  }

  const upsertMedia = async () => {
    if (mediaPreview) {
      const uploadResult = await uploadMedia(
        mediaPreview,
        mediaIdOverride ? workout.media : undefined,
      );
      setMediaPreview(null);
      if (uploadResult.public_id) {
        return uploadResult.public_id;
      }
    }
    return workout.media;
  };

  const save = async () => {
    try {
      const media = await upsertMedia();
      const editedWorkout = {
        ...workout,
        media,
      };
      if (isEdit) {
        await updateWorkout(editedWorkout);
      } else {
        const { id: newId } = await createWorkout(editedWorkout);
        if (newId) {
          navigate(`/workout/${newId}`);
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        alert(e.message);
      }
    }
  };

  return (
    <Stack spacing={2}>
      <TextField
        label="Title"
        value={workout.title}
        variant="outlined"
        onChange={(input) => {
          setWorkout({ ...workout, title: input.currentTarget.value });
        }}
      />
      <TextField
        label="Category"
        value={workout.category}
        variant="outlined"
        onChange={(input) => {
          setWorkout({ ...workout, category: input.currentTarget.value });
        }}
      />
      <TextField
        label="Description"
        value={workout.description}
        variant="outlined"
        onChange={(input) => {
          setWorkout({
            ...workout,
            description: input.currentTarget.value,
          });
        }}
      />
      <TextField
        label="Language"
        value={workout.language}
        variant="outlined"
        onChange={(input) => {
          setWorkout({ ...workout, language: input.currentTarget.value });
        }}
      />
      <TextField
        label="Objective"
        value={workout.objective}
        variant="outlined"
        onChange={(input) => {
          setWorkout({ ...workout, objective: input.currentTarget.value });
        }}
      />
      <TextField
        type="number"
        value={workout.skillLevel}
        label="Skill level"
        variant="outlined"
        onChange={(input) => {
          setWorkout({
            ...workout,
            skillLevel: parseInt(input.currentTarget.value),
          });
        }}
      />
      <TextField
        type="number"
        value={workout.intensity}
        label="Intensity"
        variant="outlined"
        onChange={(input) => {
          setWorkout({
            ...workout,
            intensity: parseInt(input.currentTarget.value),
          });
        }}
      />
      <TextField
        type="number"
        value={workout.duration}
        label="Duration"
        variant="outlined"
        onChange={(input) => {
          setWorkout({
            ...workout,
            duration: parseInt(input.currentTarget.value),
          });
        }}
      />
      <TextField
        type="number"
        value={workout.loadPoints}
        label="Load Points"
        variant="outlined"
        onChange={(input) => {
          setWorkout({
            ...workout,
            loadPoints: parseInt(input.currentTarget.value),
          });
        }}
      />
      <TextField
        type="number"
        value={workout.calories}
        label="Calories"
        variant="outlined"
        onChange={(input) => {
          setWorkout({
            ...workout,
            calories: parseInt(input.currentTarget.value),
          });
        }}
      />
      <ExerciseRows
        exerciseRows={workout.exerciseRows}
        onChange={(rows) => {
          // @ts-expect-error -- Allow partials to state in order to be able to build new rows
          setWorkout({ ...workout, exerciseRows: rows });
        }}
      />
      <MediaWithPreview
        type="image"
        label="Upload media"
        alt="Workout media"
        mediaId={workout.media}
        mediaPreview={mediaPreview}
        setMediaPreview={setMediaPreview}
        mediaIdOverride={mediaIdOverride}
        setMediaIdOverride={setMediaIdOverride}
        overrideAble={isEdit}
      />
      <Stack direction="row" spacing={2}>
        <TagsSelector
          value={workout.tags ?? []}
          onChange={(selected) => {
            console.log("Selected tags", selected);
            setWorkout({
              ...workout,
              tags: selected,
            });
          }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={workout.favorite}
              onChange={(e) =>
                setWorkout({ ...workout, favorite: e.target.checked })
              }
            />
          }
          label="Favorite"
        />
      </Stack>
      <Button variant="contained" onClick={() => save()}>
        {isEdit ? "Save" : "Create"}
      </Button>
    </Stack>
  );
};
